/**
 * Hyperliquid Trading Adapter
 *
 * Implements PerpsTradingAdapter by wrapping HLApiWalletService (plaintext)
 * or SecureHLApiWalletService (encrypted IndexedDB). Automatically selects
 * the secure variant when browser capabilities allow.
 *
 * This is plain TypeScript (no React). The hook layer reads getSetupStep()
 * and manages React state transitions.
 */

import type { WalletClient } from "viem";
import {
  HLApiWalletService,
  SecureHLApiWalletService,
  isSecureStorageAvailable,
} from "../browser/api-wallet";
import type {
  HLOrderParams,
  HLApiWalletStatus,
} from "../browser/api-wallet";
import {
  getAgentApprovalTypedData,
  submitAgentApproval,
} from "../client";
import type { HLBuilderConfig } from "../client";
import { nextNonce } from "../browser/cross-tab-sync";
import type {
  PerpsOrderParams,
  PerpsOrderResult,
  PerpsTradingSetupStep,
  PerpsSignTypedDataParams,
} from "../types/trading";
import type {
  PerpsTradingAdapter,
  PerpsTradingAdapterConfig,
  PerpsCancelResult,
  PerpsLeverageResult,
  PerpsLeverageParams,
  PerpsTriggerOrderParams,
} from "./types";

// ============================================================================
// Helpers
// ============================================================================

/**
 * Normalize a full symbol (e.g. "BTCUSDT", "BTCUSDC") to the bare coin name
 * used by HL's universe ("BTC"). Also handles already-bare names.
 */
function toHLCoin(symbol: string): string {
  return symbol.replace(/USD[TC]$/i, "");
}

// ============================================================================
// Types
// ============================================================================

type ServiceRef =
  | { secure: true; service: SecureHLApiWalletService }
  | { secure: false; service: HLApiWalletService };

// ============================================================================
// Adapter Class
// ============================================================================

export class HyperliquidTrading implements PerpsTradingAdapter {
  private serviceRef: ServiceRef | null = null;
  private setupStep: PerpsTradingSetupStep = "disconnected";
  private readonly testnet: boolean;
  private readonly builder?: HLBuilderConfig;
  private walletClient: WalletClient | null;

  constructor(config: PerpsTradingAdapterConfig) {
    this.testnet = config.testnet ?? false;
    this.builder = config.builder
      ? { address: config.builder.address, feeRate: config.builder.feeRate }
      : undefined;
    this.walletClient = config.walletClient;

    // Use VSECURE only when explicitly configured AND browser supports it
    const useSecure =
      config.authMode === "vsecure" &&
      typeof window !== "undefined" &&
      isSecureStorageAvailable();
    const opts = {
      builder: this.builder,
      testnet: this.testnet,
      dexNames: config.dexNames,
    };

    if (useSecure) {
      this.serviceRef = {
        secure: true,
        service: new SecureHLApiWalletService(opts),
      };
    } else {
      this.serviceRef = {
        secure: false,
        service: new HLApiWalletService(opts),
      };
    }
  }

  // --------------------------------------------------------------------------
  // Status
  // --------------------------------------------------------------------------

  getSetupStep(): PerpsTradingSetupStep {
    return this.setupStep;
  }

  isReady(): boolean {
    return this.setupStep === "ready";
  }

  isSecure(): boolean {
    return this.serviceRef?.secure ?? false;
  }

  // --------------------------------------------------------------------------
  // Wallet Lifecycle
  // --------------------------------------------------------------------------

  async initWallet(mainWalletAddress: string): Promise<PerpsTradingSetupStep> {
    const ref = this.serviceRef;
    if (!ref) {
      this.setupStep = "disconnected";
      return this.setupStep;
    }

    if (ref.secure) {
      if (this.walletClient) {
        const existing = await ref.service.loadWallet(
          mainWalletAddress,
          this.walletClient,
        );
        if (existing) {
          const status = await ref.service.getStatus(mainWalletAddress);
          this.updateSetupStep(status);
          return this.setupStep;
        }

        // No existing wallet — generate and encrypt a new one
        await ref.service.generateWallet(mainWalletAddress, this.walletClient);
        const status = await ref.service.getStatus(mainWalletAddress);
        this.updateSetupStep(status);
      } else {
        // No walletClient yet — check metadata only (no decrypt)
        const status = await ref.service.getStatus(mainWalletAddress);
        if (status.hasWallet) {
          this.updateSetupStep(status);
        } else {
          this.setupStep = "generate_wallet";
        }
      }
    } else {
      const existing = ref.service.loadWallet(mainWalletAddress);
      if (existing) {
        const status = ref.service.getStatus(mainWalletAddress);
        this.updateSetupStep(status);
        return this.setupStep;
      }

      ref.service.generateWallet(mainWalletAddress);
      const status = ref.service.getStatus(mainWalletAddress);
      this.updateSetupStep(status);
    }

    return this.setupStep;
  }

  async approveAgent(
    signTypedData: (params: PerpsSignTypedDataParams) => Promise<`0x${string}`>,
    mainWalletAddress: string,
  ): Promise<boolean> {
    const ref = this.serviceRef;
    if (!ref) return false;

    const walletAddress = ref.service.getWalletAddress();
    if (!walletAddress) return false;

    const nonce = nextNonce();
    const isMainnet = !this.testnet;
    const typedData = getAgentApprovalTypedData(
      walletAddress,
      "PERPSSTUDIO",
      nonce,
      isMainnet,
    );

    const signature = await signTypedData({
      domain: typedData.domain,
      types: typedData.types,
      primaryType: typedData.primaryType,
      message: typedData.message as Record<string, unknown>,
    });

    const result = await submitAgentApproval(
      signature,
      walletAddress,
      "PERPSSTUDIO",
      nonce,
      isMainnet,
    );

    if (result.success) {
      if (ref.secure) {
        await ref.service.markApproved(mainWalletAddress);
        const status = await ref.service.getStatus(mainWalletAddress);
        this.updateSetupStep(status);
      } else {
        ref.service.markApproved(mainWalletAddress);
        const status = ref.service.getStatus(mainWalletAddress);
        this.updateSetupStep(status);
      }
      return true;
    }

    return false;
  }

  // --------------------------------------------------------------------------
  // Order Placement
  // --------------------------------------------------------------------------

  async placeMarketOrder(params: PerpsOrderParams): Promise<PerpsOrderResult> {
    const ref = this.serviceRef;
    if (!ref) return { success: false, error: "Service not initialized" };

    const hlParams: HLOrderParams = {
      coin: toHLCoin(params.symbol),
      side: params.side,
      size: params.size,
      reduceOnly: params.reduceOnly,
      maxSlippage: params.maxSlippage,
      includeBuilderFee: !!this.builder,
    };
    const result = await ref.service.placeMarketOrder(hlParams);
    return {
      success: result.success,
      orderId: result.orderId,
      error: result.error,
    };
  }

  async placeLimitOrder(params: PerpsOrderParams): Promise<PerpsOrderResult> {
    const ref = this.serviceRef;
    if (!ref) return { success: false, error: "Service not initialized" };

    const hlParams: HLOrderParams = {
      coin: toHLCoin(params.symbol),
      side: params.side,
      size: params.size,
      price: params.price,
      reduceOnly: params.reduceOnly,
      includeBuilderFee: !!this.builder,
    };
    const result = await ref.service.placeLimitOrder(hlParams);
    return {
      success: result.success,
      orderId: result.orderId,
      error: result.error,
    };
  }

  async placeTriggerOrder(
    params: PerpsTriggerOrderParams,
  ): Promise<PerpsOrderResult> {
    const ref = this.serviceRef;
    if (!ref) return { success: false, error: "Service not initialized" };

    const isMarket =
      params.orderType === "stop_market" || params.orderType === "take_profit";
    const tpsl: "tp" | "sl" =
      params.orderType === "take_profit" || params.orderType === "stop_loss"
        ? params.orderType === "take_profit"
          ? "tp"
          : "sl"
        : "tp";

    const result = await ref.service.placeTriggerOrder({
      coin: toHLCoin(params.symbol),
      side: params.side,
      size: params.size,
      triggerPrice: params.triggerPrice,
      isMarket,
      tpsl,
      reduceOnly: params.reduceOnly,
      price: params.price,
      includeBuilderFee: !!this.builder,
    });

    return {
      success: result.success,
      orderId: result.orderId,
      error: result.error,
    };
  }

  // --------------------------------------------------------------------------
  // Order Management
  // --------------------------------------------------------------------------

  async cancelOrder(
    symbol: string,
    orderId: number,
  ): Promise<PerpsCancelResult> {
    const ref = this.serviceRef;
    if (!ref) return { success: false, error: "Service not initialized" };
    return ref.service.cancelOrder(toHLCoin(symbol), orderId);
  }

  async updateLeverage(
    params: PerpsLeverageParams,
  ): Promise<PerpsLeverageResult> {
    const ref = this.serviceRef;
    if (!ref) return { success: false, error: "Service not initialized" };
    return ref.service.updateLeverage({
      asset: toHLCoin(params.symbol),
      leverage: params.leverage,
      isCross: params.isCross,
    });
  }

  // --------------------------------------------------------------------------
  // Cleanup
  // --------------------------------------------------------------------------

  async clearWallet(mainWalletAddress: string): Promise<void> {
    const ref = this.serviceRef;
    if (!ref) return;

    if (ref.secure) {
      await ref.service.clearWallet(mainWalletAddress);
    } else {
      ref.service.clearWallet(mainWalletAddress);
    }

    this.setupStep = "generate_wallet";
  }

  destroy(): void {
    if (this.serviceRef?.secure) {
      this.serviceRef.service.destroy();
    }
    this.serviceRef = null;
    this.setupStep = "disconnected";
  }

  // --------------------------------------------------------------------------
  // Internal
  // --------------------------------------------------------------------------

  private updateSetupStep(status: HLApiWalletStatus | null): void {
    if (!status?.hasWallet) {
      this.setupStep = "generate_wallet";
    } else if (status.needsApproval) {
      this.setupStep = "approve_agent";
    } else {
      this.setupStep = "ready";
    }
  }
}
